import { getAllFinanceTypesForCar } from '@upstart/harmony/lib/deal-utils';
import { SelectedAccessory } from '@upstart/harmony/lib/legos/accessoryTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import {
  Condition,
  CrmType,
  FAUX_TRADE_IN_ID,
  FinanceType,
  UserType,
} from '@upstart/harmony/lib/legos/constants';
import {
  Dealer,
  DealerWithFlags,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { Deal, TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import {
  CashOfferData,
  DeskingOffer,
  LeaseOfferData,
  OfferData,
  PurchaseOfferData,
} from '@upstart/harmony/lib/legos/deskingTypes';
import {
  Pencil,
  ProtectionPlanProductPriceOverrides,
} from '@upstart/harmony/lib/legos/pencilTypes';
import { Bundle, SelectedProduct } from '@upstart/harmony/lib/legos/penTypes';
import _, { cloneDeep, isEqual, sumBy } from 'lodash';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import validation from '@upstart/harmony/lib/validation';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import { FinanceApplication } from '@upstart/harmony/lib/legos/financingTypes';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import { getClientType } from './utils';
import {
  ErrorMap,
  TradeInError,
  TradeInField,
} from '../components/DeskingEditor/TradeInsModal/TradeInsTableRow';
import { Desking } from '../components/DeskingEditor/types';
import { fauxTradeId } from './constants';
import { Feature, GrowthBook } from '../hooks/GrowthBook';

/**
 * Takes the selected bundles, products, and accessories from each deskingOffer
 * and builds the maps for Pencil.data.selectedBundles and Pencil.data.selectedProducts.
 * This is done to ensure backward compatibility with V1 code.
 */
const buildSelectedOptions = (deskingOffers: DeskingOffer[]) => {
  const holderObj: {
    selectedAccessories?: SelectedAccessory[];
    selectedBundles?: {
      [index in FinanceType]?: Bundle[];
    };
    selectedProducts?: {
      [index in FinanceType]?: SelectedProduct[];
    };
  } = {};
  deskingOffers.forEach(deskingOffer => {
    const financeType = deskingOffer.data?.financeType;
    if (financeType) {
      const accessories = deskingOffer.data?.selectedAccessories;
      // accessories should be the same for all offers, only copy them to holderObj once
      if (accessories && !isDefined(holderObj.selectedAccessories)) {
        holderObj.selectedAccessories = accessories;
      }
      const bundles = deskingOffer.data?.selectedBundles;
      if (bundles) {
        if (!holderObj.selectedBundles) {
          holderObj.selectedBundles = {};
        }
        holderObj.selectedBundles[financeType] = bundles;
      }
      const products = deskingOffer.data?.selectedProducts;
      if (products) {
        if (!holderObj.selectedProducts) {
          holderObj.selectedProducts = {};
        }
        holderObj.selectedProducts[financeType] = products;
      }
    }
  });
  return holderObj;
};

const getTradeInValues = (offer: DeskingOffer) => {
  const tradeIn = {
    pencilTradeIns: offer.data?.pencilTradeIns || [],
    tradeInAllowance: offer.data?.tradeInAllowance || 0,
    tradeInTaxCredit: offer.data?.tradeInTaxCredit || false,
    tradePayoff: offer.data?.tradePayoff || 0,
    tradeACV: offer.data?.tradeACV || 0,
  };
  return tradeIn;
};

export const createPencilFromDesking = (
  currentPencil: Pencil,
  desking: Desking,
  dealer: Dealer,
) => {
  const pencil = { ...currentPencil };
  if (pencil.data) {
    // replace pencil offers with those from desking, including the deskingOfferId in the process
    pencil.data.purchase = desking.deskingOffers
      .filter(offer => offer.data?.financeType === FinanceType.PURCHASE)
      .map(
        offer =>
          ({ deskingOfferId: offer.id, ...offer.data } as PurchaseOfferData),
      );
    pencil.data.lease = desking.deskingOffers
      .filter(offer => offer.data?.financeType === FinanceType.LEASE)
      .map(
        offer =>
          ({ deskingOfferId: offer.id, ...offer.data } as LeaseOfferData),
      );
    pencil.data.cash = desking.deskingOffers
      .filter(offer => offer.data?.financeType === FinanceType.CASH)
      .map(
        offer => ({ deskingOfferId: offer.id, ...offer.data } as CashOfferData),
      );
    // copy selected bundles and products
    pencil.data = _.omit(pencil.data, [
      'selectedAccessories',
      'selectedBundles',
      'selectedProducts',
    ]);
    const isPencilTradeInsModified = !isEqual(
      pencil?.data?.pencilTradeIns,
      desking.deskingOffers[0]?.data?.pencilTradeIns,
    );
    pencil.data = {
      ...pencil.data,
      ...buildSelectedOptions(desking.deskingOffers),
      show10DollarMonthlyPriceRange: shouldShowMonthlyPriceRange(
        desking,
        dealer,
      ),
      ...(isPencilTradeInsModified && {
        ...getTradeInValues(desking.deskingOffers[0]),
      }),
    };
  }
  return pencil;
};

export function shouldShowMonthlyPriceRange(desking: Desking, dealer: Dealer) {
  return desking.deskingOffers
    .filter(
      o =>
        o.data?.financeType === FinanceType.LEASE ||
        o.data?.financeType === FinanceType.PURCHASE,
    )
    .every(
      o =>
        o.data?.show10DollarMonthlyPriceRange ??
        dealer.desking?.show10DollarMonthlyPriceRangeByDefault,
    );
}

export function shouldDisplayFinanceType(
  car: Car,
  deal: Deal,
  dealer: Dealer,
  financeType: FinanceType | undefined,
) {
  if (!financeType) {
    return false;
  }
  const applicableFinanceTypes = getAllFinanceTypesForCar({
    car,
    deal,
    dealer,
    clientType: getClientType(deal),
  });
  if (!applicableFinanceTypes.includes(financeType)) {
    return false;
  }
  if (financeType === FinanceType.LEASE) {
    return car?.condition === Condition.NEW;
  }
  return true;
}

export function getApplicableDeskingOffers(
  car: Car,
  deal: Deal,
  dealer: Dealer,
  desking: Desking,
) {
  const allDeskingOffers = desking?.deskingOffers ?? [];
  const applicableDeskingOffers = allDeskingOffers.filter(o =>
    shouldDisplayFinanceType(car, deal, dealer, o?.data?.financeType),
  );

  return applicableDeskingOffers;
}

export function showBiweeklyPayments(dealer?: Dealer) {
  return dealer?.desking?.showBiweeklyPayments ?? false;
}

export function hasSameBundles(
  deskingOffer: DeskingOffer,
  selectedBundles: Bundle[],
) {
  const offerBundleIds = (deskingOffer.data?.selectedBundles ?? [])
    .map(bundle => bundle.id)
    .sort();
  const selectedBundleIds = selectedBundles.map(bundle => bundle.id).sort();
  return isEqual(offerBundleIds, selectedBundleIds);
}

export function hasSameProducts(
  deskingOffer: DeskingOffer,
  selectedProducts: SelectedProduct[],
) {
  const offerProductIds = (deskingOffer.data?.selectedProducts ?? [])
    .map(product => product.productId)
    .sort();
  const selectedProductIds = selectedProducts
    .map(product => product.productId)
    .sort();
  return isEqual(offerProductIds, selectedProductIds);
}

export function hasSameProductPriceOverrides(
  deskingOffer: DeskingOffer,
  priceOverrides: ProtectionPlanProductPriceOverrides,
) {
  const offerProductPriceOverrides =
    deskingOffer.data?.protectionPlanProductPriceOverrides ?? {};
  const selectedProductPriceOverrides = priceOverrides ?? {};
  return isEqual(offerProductPriceOverrides, selectedProductPriceOverrides);
}

export function canUpdateTradeIns(dealer?: DealerWithFlags): boolean {
  if (!dealer) {
    return false;
  }
  const isDgdgSalesforce = dealer.crmType === CrmType.DGDG_SALESFORCE;
  const isDealersocket = dealer.crmType === CrmType.DEALERSOCKET;
  const isVinsolutions = dealer.crmType === CrmType.VINSOLUTIONS;
  const isStandalone = dealer.crmType === CrmType.STANDALONE;
  const isElead = dealer.crmType === CrmType.ELEAD;
  const isNewElead = !!dealer.flags?.isNewElead;
  const isNewVinsolutions = !!dealer.flags?.isNewVinsolutions;

  return (
    (isElead && isNewElead) ||
    (isVinsolutions && isNewVinsolutions) ||
    isDgdgSalesforce ||
    isDealersocket ||
    isStandalone ||
    !dealer.crmType
  );
}
export const determineTargetState = (
  deal: Deal,
  dealer: Dealer,
  offer: DeskingOffer,
): string | null | undefined => {
  if (offer.data?.taxBreakdown?.taxByDealerAddress) {
    return dealer.state;
  }

  return deal.applicant?.state ?? dealer.state;
};

export const canPushTradeInsToDMS = (
  tradeIns: TradeIn[],
  dmsType?: DMSType | null,
) => {
  // if only has empty faux trade in allow push without showing modal
  if (
    tradeIns.length === 1 &&
    tradeIns[0].id === FAUX_TRADE_IN_ID &&
    !tradeIns[0].tradeInAllowance &&
    !tradeIns[0].payOff &&
    !tradeIns[0].tradeACV
  ) {
    return true;
  }

  return tradeIns.every(
    tradeIn =>
      (dmsType === DMSType.CDK || !!tradeIn.id) &&
      !!tradeIn.year?.name &&
      !!tradeIn.make?.name &&
      !!tradeIn.model?.name,
  );
};

export const validateTradeIns = (
  updatedTradeIns: TradeIn[],
  tradeInFields: TradeInField[],
) => {
  const errors: ErrorMap = {};
  const vinList: string[] = []; // we use this array to check for duplicates
  updatedTradeIns.forEach((tradeIn, index) => {
    const tradeInError: TradeInError = {};
    if (tradeIn.id) {
      if (vinList.includes(tradeIn.id)) {
        tradeInError.vin = 'Duplicate VIN';
      } else if (!validation.isVin(`${tradeIn.id}`.toUpperCase())) {
        tradeInError.vin = 'Invalid VIN';
      } else {
        vinList.push(tradeIn.id);
      }
    } else if (tradeInFields.find(t => t.key === 'id')?.required) {
      tradeInError.vin = 'Required';
    }

    if (
      !tradeIn.year?.name &&
      tradeInFields.find(t => t.key === 'year')?.required
    ) {
      tradeInError.year = 'Required';
    }
    if (
      !tradeIn.make?.name &&
      tradeInFields.find(t => t.key === 'make')?.required
    ) {
      tradeInError.make = 'Required';
    }
    if (
      !tradeIn.model?.name &&
      tradeInFields.find(t => t.key === 'model')?.required
    ) {
      tradeInError.model = 'Required';
    }
    if (
      !tradeIn.mileage &&
      tradeInFields.find(t => t.key === 'mileage')?.required
    ) {
      tradeInError.mileage = 'Required';
    }
    if (
      !tradeIn.tradeValue &&
      tradeInFields.find(t => t.key === 'tradeValue')?.required
    ) {
      tradeInError.value = 'Required';
    }
    if (
      !tradeIn.tradeInAllowance &&
      tradeInFields.find(t => t.key === 'tradeInAllowance')?.required
    ) {
      tradeInError.allowance = 'Required';
    }

    if (Object.keys(tradeInError).length > 0) {
      errors[index] = tradeInError;
    }
  });
  return errors;
};

export const findTradeIn = (tradeIns: TradeIn[], tradeInToCheck: TradeIn) =>
  tradeIns.find(t => {
    if (tradeInToCheck.id) {
      return t.id === tradeInToCheck.id;
    }
    return (
      t.year?.name === tradeInToCheck.year?.name &&
      t.make?.name === tradeInToCheck.make?.name &&
      t.model?.name === tradeInToCheck.model?.name &&
      t.trim?.name === tradeInToCheck.trim?.name
    );
  });

export const setAllowanceOnTradeIns = (tradeIns: TradeIn[]): TradeIn[] => {
  const updatedTradeIns = cloneDeep(tradeIns);
  // if tradeInAllowance is not set but tradeValue is, set allowance = value
  updatedTradeIns.forEach(tradeIn => {
    if (
      tradeIn.id !== fauxTradeId &&
      tradeIn.tradeValue &&
      !tradeIn.tradeInAllowance
    ) {
      tradeIn.tradeInAllowance = tradeIn.tradeValue;
    }
  });

  return updatedTradeIns;
};

export const updateDeskingTradeIns = (
  newTradeIns: TradeIn[],
  deskingOffers: DeskingOffer[],
): DeskingOffer[] => {
  const updatedTradeIns = setAllowanceOnTradeIns(newTradeIns);
  const updatedDeskingOffers = deskingOffers.map(deskingOffer =>
    // update trade-in values
    ({
      ...deskingOffer,
      sentToSalespersonAt: null,
      data: {
        ...deskingOffer.data,
        pencilTradeIns: updatedTradeIns,
        tradeInAllowance: sumBy(updatedTradeIns, 'tradeInAllowance'),
        tradePayoff: sumBy(updatedTradeIns, 'payOff'),
        tradeACV: sumBy(updatedTradeIns, 'tradeACV'),
      } as OfferData,
    }),
  );

  return updatedDeskingOffers;
};

export async function shouldRedirectToDeskingV3({
  user,
  gb,
}: {
  user: User;
  gb: GrowthBook;
}): Promise<boolean> {
  // this is to prevent a redirection loop when accessing history and notes iframe on Desking V3
  if (window.location.search.includes('history-and-notes')) {
    return false;
  }
  if (user?.type !== UserType.CLIENT) {
    return false;
  }
  await gb.setAttributes({ dealerId: user.dealer_id });
  return gb.getFeatureValue(Feature.DESKING_V3, false);
}

export function shouldShowFinanceTab({
  financeApplications,
  deal,
  gb,
}: {
  financeApplications: FinanceApplication[] | null;
  deal: Deal;
  gb: GrowthBook;
}) {
  const shouldHideForDv2 = gb.getFeatureValue(
    Feature.HIDE_FINANCE_OFFERS_TAB,
    false,
  );

  return (
    ((financeApplications?.length ?? 0) > 0 || !!deal.submittedToLendersAt) &&
    !shouldHideForDv2
  );
}
